import { Injectable } from '@angular/core';
import { AppUser, IAppUser } from '../models/app-user.model';
import { Administrator, IAdministrator } from '../models/administrator.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';

@Injectable({
  providedIn: 'root'
})
export class AdministratorService {

  public loading: boolean = false;
  public administrators: Administrator[] = [];

  /** Flag propio de la lectura del registro propio: no toca `loading`, que es
      el de la tabla de la pantalla de sudo. */
  public loadingPreferences: boolean = false;

  /** Registro de administrador del usuario autenticado en el fraccionamiento. */
  public preferences?: Administrator;

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR create                               */
  /* -------------------------------------------------------------------------- */
  // region create
  async create(
    neighborhoodId: string,
    email: string
  ): Promise<AppUser | undefined> {
      
    let appUser = undefined;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/administrators`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Vinculando usuario';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify({
              email
            }),
            {
              headers: {
                'Authorization': token.toString(),
                'content-type': 'application/json'
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 201) {

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          neighborhoodId
        );

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return appUser;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR delete                               */
  /* -------------------------------------------------------------------------- */
  // region delete
  async delete(
    neighborhoodId: string,
    uidGuard: string
  ): Promise<boolean> {

    let flag = false;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Quitando administrador';

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/administrators/${uidGuard}`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            {
              headers: {
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {
        flag = true;
        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          neighborhoodId
        );
      } else {
        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );
      }

    } catch (error) {
      console.log(error);
      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR getAll                               */
  /* -------------------------------------------------------------------------- */
  // region getAll
  async getAll(
    neighborhoodId: string
  ): Promise<Administrator[]> {

    let administrators: Administrator[] = [];

    this.loading = true;
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/administrators`;

    try {

      const token = await this.authService.getToken();
      
      const response = await new Promise<ServerResponse<{ administrators: IAdministrator[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ administrators: IAdministrator[] }>>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => reject(error),
          });
        }
      );

      if (response.statusCode === 200) {
        this.administrators = response.data!.administrators.map(
          administrator => new Administrator(administrator)
        );

        administrators = this.administrators;
      }

    } catch(error) {
      this.toastService.show({
        header: 'Error de servidor',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.loading = false;

    return administrators;

  }
  

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR getPreferences                           */
  /* -------------------------------------------------------------------------- */
  // region getPreferences
  /**
   * Registro de administrador del usuario autenticado. Existe aparte de
   * `getAll` porque ese listado es solo para sudo: un administrador normal
   * recibe 403 y nunca podría leer su propia configuración.
   *
   * No enciende el spinner global: quien lo llama es un modal que ya muestra su
   * propio esqueleto.
   */
  async getPreferences(
    neighborhoodId: string
  ): Promise<Administrator | undefined> {

    let administrator: Administrator | undefined;

    this.loadingPreferences = true;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/administrators/preferences`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ administrator: IAdministrator }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ administrator: IAdministrator }>>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if (response.statusCode === 200) {
        administrator = new Administrator(response.data!.administrator);
        this.preferences = administrator;
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error de servidor',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.loadingPreferences = false;

    return administrator;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR update                               */
  /* -------------------------------------------------------------------------- */
  // region update
  async update(
    neighborhoodId: string,
    uidAdministrator: string,
    administrator: {
      isOwner: boolean,
      paymentsNotification: boolean,
      reportsNotification: boolean
    }
  ): Promise<boolean> {

    let flag = false;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando administrador';

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/administrators/${uidAdministrator}`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(administrator),
            {
              headers: {
                'Authorization': token.toString(),
                'content-type': 'application/json'
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {
        flag = true;
        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          neighborhoodId
        );
      } else {
        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );
      }

    } catch (error) {
      console.log(error);
      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR updatePreferences                          */
  /* -------------------------------------------------------------------------- */
  // region updatePreferences
  /**
   * Guarda las preferencias propias. El endpoint no lleva uid: el backend
   * resuelve al usuario autenticado.
   *
   * A diferencia de `update`, NO llama a `getAll`: ese listado es de sudo y un
   * administrador normal recibiría un 403 justo después de guardar bien.
   */
  async updatePreferences(
    neighborhoodId: string,
    preferences: {
      reportsNotification: boolean
    }
  ): Promise<boolean> {

    let flag = false;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Guardando preferencias';

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/administrators/preferences`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ administrator: IAdministrator }>>(
        (resolve, reject) => {
          this.http.put<IServerResponse<{ administrator: IAdministrator }>>(
            urlApi,
            JSON.stringify(preferences),
            {
              headers: {
                'Authorization': token.toString(),
                'content-type': 'application/json'
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {
        flag = true;

        this.preferences = new Administrator(response.data!.administrator);

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );
      } else {
        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );
      }

    } catch (error) {
      console.log(error);
      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;

  }

}
